import React from 'react';
import { Title } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
    BrandArchitecture,
    Logo,
    Card,
    ButtonField,
    Icon,
} from '@wk/components-v3-react16';
import { useT } from './i18n/useT';
import type { AppTranslationKey } from './i18n/types';

type ActionCardProps = {
    iconName: string;
    title: string;
    description: string;
    actionLabel: string;
    to: string;
};

const ActionCard = ({ iconName, title, description, actionLabel, to }: ActionCardProps) => {
    const navigate = useNavigate();

    return (
        <Card mode="interactive" flowClass="faust-action-card">
            <div
                onClick={() => navigate(to)}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    padding: 20,
                    height: '100%',
                    cursor: 'pointer',
                }}
            >
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background: 'rgba(22, 50, 79, 0.08)',
                        color: '#16324f',
                    }}
                >
                    <Icon name={iconName} srTitle={title} isDecorativeOnly />
                </span>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#16324f' }}>{title}</span>
                <span style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(22, 50, 79, 0.72)', flex: 1 }}>
                    {description}
                </span>
                <ButtonField type="text" size="small">
                    <button type="button" onClick={(e) => { e.stopPropagation(); navigate(to); }}>
                        {actionLabel}
                    </button>
                </ButtonField>
            </div>
        </Card>
    );
};

export const AdminDashboard = () => {
    const navigate = useNavigate();
    const t = useT();

    const cards: Array<{
        key: 'publications' | 'queue' | 'files' | 'bulkUpload' | 'verification' | 'fingerprint' | 'users' | 'operations';
        iconName: string;
        to: string;
    }> = [
        { key: 'publications', iconName: 'wk-icon-book', to: '/publications' },
        { key: 'queue', iconName: 'wk-icon-process-list', to: '/queue' },
        { key: 'files', iconName: 'wk-icon-files', to: '/files' },
        { key: 'bulkUpload', iconName: 'wk-icon-upload-line', to: '/mass-upload' },
        { key: 'verification', iconName: 'wk-icon-check-circle', to: '/verify' },
        { key: 'fingerprint', iconName: 'wk-icon-file-compare', to: '/fingerprint-verify' },
        { key: 'users', iconName: 'wk-icon-users', to: '/users' },
        { key: 'operations', iconName: 'wk-icon-settings', to: '/catalog-sync' },
    ];

    return (
        <div className="cg3-typography" style={{ padding: '24px clamp(16px, 3vw, 32px)', maxWidth: 1520, margin: '0 auto' }}>
            <Title title={t('app.brand')} />

            <Card flowClass="faust-hero-card">
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 32,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 'clamp(24px, 3vw, 40px)',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
                        <BrandArchitecture
                            href="/"
                            suite={t('app.suite')}
                            product={t('app.brand')}
                            isCompact
                            srLabel={t('app.brand')}
                        />
                        <h1 style={{ margin: 0, fontSize: 'clamp(28px, 3vw, 40px)', fontWeight: 800, lineHeight: 1.1, color: '#16324f' }}>
                            {t('dashboard.heroTitle')}
                        </h1>
                        <p style={{ margin: 0, maxWidth: 700, fontSize: 16, lineHeight: 1.6, color: 'rgba(22, 50, 79, 0.82)' }}>
                            {t('dashboard.heroSubtitle')}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 4 }}>
                            <ButtonField type="primary" size="medium">
                                <button type="button" onClick={() => navigate('/queue')}>{t('dashboard.actionOpenQueue')}</button>
                            </ButtonField>
                            <ButtonField type="secondary" size="medium">
                                <button type="button" onClick={() => navigate('/publications')}>{t('dashboard.actionBrowsePublications')}</button>
                            </ButtonField>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-end', minWidth: 240 }}>
                        <Logo alt={t('app.suite')} size="medium" />
                    </div>
                </div>
            </Card>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: 16,
                    marginTop: 24,
                }}
            >
                {cards.map(card => (
                    <ActionCard
                        key={card.key}
                        iconName={card.iconName}
                        title={t(`dashboard.cards.${card.key}.title` as AppTranslationKey)}
                        description={t(`dashboard.cards.${card.key}.description` as AppTranslationKey)}
                        actionLabel={t(`dashboard.cards.${card.key}.action` as AppTranslationKey)}
                        to={card.to}
                    />
                ))}
            </div>
        </div>
    );
};
